import { config } from '../core/config'
import { storage } from '@/utils/storage/storage'

const EKP_ORIGIN_SESSION_KEY = 'ekpOrigin'

function getSessionStorage(): Storage | undefined {
  if (typeof window === 'undefined') {
    return
  }

  try {
    return window.sessionStorage
  } catch (error) {
    return
  }
}

function normalizeEkpOrigin(origin: string) {
  const trimmed = origin.trim()

  if (!trimmed) {
    return
  }

  const hasProtocol = /^https?:\/\//i.test(trimmed)

  const withProtocol = hasProtocol ? trimmed : `https://${trimmed}`

  return withProtocol.replace(/\/+$/, '')
}

function readEkpOrigin() {
  const storage = getSessionStorage()

  if (!storage) {
    return
  }

  try {
    return storage.getItem(EKP_ORIGIN_SESSION_KEY) ?? undefined
  } catch (error) {
    return
  }
}

export function setEkpOrigin(origin?: string | null) {
  const storage = getSessionStorage()

  if (!storage) {
    return
  }

  const normalizedOrigin = origin ? normalizeEkpOrigin(origin) : undefined

  if (!normalizedOrigin) {
    storage.removeItem(EKP_ORIGIN_SESSION_KEY)

    return
  }

  storage.setItem(EKP_ORIGIN_SESSION_KEY, normalizedOrigin)
}

export function getEkpOrigin() {
  return readEkpOrigin()
}

export function environment(): 'local' | 'feature' | 'production' | 'staging' {
  return import.meta.env.VITE_ENVIRONMENT
}

export function getBranchName() {
  return import.meta.env.VITE_CI_COMMIT_REF_NAME
}

export function getCommitHash() {
  return import.meta.env.VITE_CI_COMMIT_SHA
}

export function getPipelineId() {
  return import.meta.env.VITE_CI_PIPELINE_ID
}

export function getEKPUrl() {
  const origin = getEkpOrigin()

  if (origin) {
    return origin
  }

  if (isMyEasybellCom()) {
    return config.EKP_URL_COM[environment()]
  }

  return config.EKP_URL[environment()]
}

export function getProvisioningUrl() {
  return config.PROVISIONING_URL[environment()]
}

export function isFeature() {
  return environment() === 'feature'
}

export function isStaging() {
  return environment() === 'staging'
}

export function isLocalhostURL() {
  return window.location.hostname === 'localhost'
}

export function isMyEasybellCom() {
  try {
    const whoamiStr = storage.whoami

    if (whoamiStr) {
      const whoami = JSON.parse(whoamiStr)

      if (whoami?.customer && typeof whoami.customer === 'string') {
        return whoami.customer.startsWith('C')
      }
    }
  } catch (error) {
    return false
  }

  return false
}

export function isTesting() {
  if (isStaging() && isLocalhostURL()) {
    return true
  }
  if (isFeature() && isLocalhostURL()) {
    return true
  }
  if (window.Cypress) {
    return true
  }
  return isLocal()
}

export function isProduction() {
  return environment() === 'production'
}

export function isLocal() {
  return environment() === 'local'
}

export function directorToken() {
  return import.meta.env.VITE_DIRECTOR_TOKEN
}
